import { useQuery } from '@tanstack/react-query'
import { ViewShift } from '@xeetr/types/models'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { ShiftService } from '../services/Shift.service'

import { useTenantRequiredService } from './common/useServiceFactory'

interface UseShiftsReturn {
  shifts: ViewShift[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for fetching all shifts for the active tenant.
 *
 * Uses React Query to manage:
 * - Data fetching and caching
 * - Loading states
 * - Error handling
 * - Background refetching
 *
 * @returns Object containing shifts array, loading state, and error
 *
 * @example
 * ```tsx
 * const { shifts, isLoading } = useShifts()
 *
 * if (isLoading) return <LoadingSpinner />
 *
 * return shifts.map(shift => <ShiftItem key={shift.id} shift={shift} />)
 * ```
 */
export const useShifts = (): UseShiftsReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const getService = useTenantRequiredService(ShiftService)

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.shifts.all(tenantId),
    queryFn: async () => {
      const service = await getService()
      return service.getShifts()
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!tenantId,
  })

  return {
    shifts: data ?? [],
    isLoading,
    error: error as Error | null,
  }
}
